import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { InlineHelp } from '../../../components';
import { JSONForm } from '../../../components/json-form/JSONForm';
import { TranslationKey, useTranslation } from '../../../translations';

export enum ProductType {
  Digital,
  Physical,
  Registration,
  Vendor,
}

const productTypes: {
  canUseEventStore: boolean;
  description: TranslationKey;
  key: ProductType;
  name: TranslationKey;
}[] = [
  {
    canUseEventStore: true,
    description: 'product_types.digital.description',
    key: ProductType.Digital,
    name: 'product_types.digital.name',
  },
  {
    canUseEventStore: true,
    description: 'product_types.physical.description',
    key: ProductType.Physical,
    name: 'product_types.physical.name',
  },
  {
    canUseEventStore: false,
    description: 'product_types.vendor.description',
    key: ProductType.Vendor,
    name: 'product_types.vendor.name',
  },
];

export const CreateCategory: FC<{
  readonly forcedType?: ProductType;
}> = ({ forcedType }) => {
  const { ts } = useTranslation();
  const [productType, setProductType] = useState(forcedType ?? ProductType.Digital);
  const activeProductType = productTypes.find(({ key }) => key === productType)!;
  const history = useHistory();

  return (
    <Col lg={8} xs={12}>
      <Card>
        <CardBody>
          <h4>{ts('common.new_category')}</h4>
          <hr />
          <JSONForm<{ id: number }>
            id="createCategoryForm"
            method="post"
            onSuccess={(data) => {
              toast.success(ts('new_category_has_been_created'));
              history.push(`${history.location.pathname}/category/${data.id}`);
            }}
            path="/api/products/categories"
          >
            <Row className="justify-content-center">
              <Col lg={6} xs={12}>
                <FormGroup>
                  <Label for="name">{ts('name')}</Label>
                  <Input id="name" name="name" placeholder={`Category ${ts('name')}`} required />
                </FormGroup>
              </Col>
              <Col lg={6} xs={12} />
              <Col lg={6} xs={12}>
                <FormGroup>
                  <Label for="productType">
                    {ts('type_of_products')}
                    <InlineHelp>
                      <Label>{ts('product_type_information')}</Label>
                      <ul className="flushLeft">
                        {productTypes.map((pt) => (
                          <li key={pt.key}>{ts(pt.description)}</li>
                        ))}
                      </ul>
                    </InlineHelp>
                  </Label>
                  <Input
                    defaultValue={productType}
                    disabled={!!forcedType}
                    id="productType"
                    name="productType"
                    onChange={({ currentTarget: { value } }) => {
                      setProductType(productTypes.find(({ key }) => key.toString() === value)!.key);
                    }}
                    type="select"
                  >
                    {productTypes.map((pt) => (
                      <option key={pt.key} value={pt.key}>
                        {ts(pt.name)}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col lg={6} xs={12}>
                <Label>{ts('product_type_description')}</Label>
                <p>{ts(activeProductType.description)}</p>
              </Col>
              {activeProductType.canUseEventStore && (
                <Col className="mb-10" lg={6} xs={12}>
                  <FormGroup check className="mt-10">
                    <Input id="eventStore" name="eventStore" type="checkbox" />
                    <Label check for="eventStore">
                      {ts('list_this_category_in_the')}
                      <InlineHelp>
                        <p>{ts('checking_this_box_will_cause')}</p>
                      </InlineHelp>
                    </Label>
                  </FormGroup>
                </Col>
              )}
              <Col lg={6} xs={12} />
              <Col lg={8} xs={12}>
                <Button block color="primary" id="submitNewCategory">
                  {ts('create_category')}
                </Button>
              </Col>
            </Row>
          </JSONForm>
        </CardBody>
      </Card>
    </Col>
  );
};
